import { config } from '@config/env';
import { fleetPerformanceDataService } from '../services/fleetPerformanceDataService';
import { performanceDataService } from '../services/performanceDataService';
import { arrowStreamClient, type EventsAggregateReadyMessage } from '../services/arrowStreamClient';
import { setLiveAggregateHighlightEvents } from '../store/selectionStore';
import { debug, warn } from './console';
import { LIVE_PUSH_BACKSTOP_MS } from './livePushBackstop';
import {
  collectEventIds,
  inferLivePerformanceUwDwFromLatestRows,
  latestEventIdByDatetime,
  mergeAggregateRows,
  passesLivePerformanceTwaGate,
  type LivePerformanceAggregateRow,
} from './livePerformanceAggregateMerge';

export interface LivePerformanceStreamBridgeOptions {
  getProjectId: () => number;
  getUwDw: () => string;
  getChannelMapping: () => Record<string, unknown>;
  getChannelsByType: () => { AVG: string[]; STD: string[]; AAV: string[] };
  buildFilters: () => Record<string, unknown[]> | undefined;
  getTargets: () => { name: string; data: Record<string, unknown> };
  getAggregatesAVG: () => LivePerformanceAggregateRow[];
  getAggregatesSTD: () => LivePerformanceAggregateRow[];
  getAggregatesAAV: () => LivePerformanceAggregateRow[];
  setAggregatesAVG: (rows: LivePerformanceAggregateRow[]) => void;
  setAggregatesSTD: (rows: LivePerformanceAggregateRow[]) => void;
  setAggregatesAAV: (rows: LivePerformanceAggregateRow[]) => void;
  setAggregates: (rows: LivePerformanceAggregateRow[]) => void;
  setSelectedEvents: (ids: number[]) => void;
  setHasSelection: (value: boolean) => void;
  setUpdateCharts: (value: boolean) => void;
  /**
   * Gates the live-highlight emphasis layer (the "latest event" highlight). When
   * provided and returns false, the latest event is NOT highlighted. Tracks the
   * page's Follow switch: while paused the highlight must stay put so the page
   * behaves like a non-live Performance page. Defaults to always-active when omitted.
   */
  isHighlightActive?: () => boolean;
  /**
   * Live transport gate. False = the user has PAUSED: no pushed or polled rows may be merged, no
   * charts redrawn. Frames are simply not consumed. `events_aggregate_ready` has no replay, but a
   * skipped id is never added to `knownEventIds`, so the resume-side `catchUpLivePerformanceOnce`
   * re-discovers every missed id over REST — a pause is lossless.
   * Defaults to always-allowed when omitted.
   */
  isUpdateAllowed?: () => boolean;
  setUwDw?: (mode: 'UPWIND' | 'DOWNWIND') => void;
  /**
   * Gates the automatic UPWIND/DOWNWIND switch when the newest incoming point changes point
   * of sail. This tracks follow/playback: switching point of sail clears the selection, so
   * while the user is paused with a manual selection the switch must not fire (otherwise
   * every tack/gybe wipes their selection). Rows that the current TWA gate then rejects are
   * left out of `knownEventIds` so they are re-discovered once follow resumes.
   * Defaults to always-allowed when omitted.
   */
  shouldAutoSwitchUwDw?: () => boolean;
  onActiveChange?: (active: boolean) => void;
  /** Live session date (YYYYMMDD) for REST poll fallback when WebSockets are disabled. */
  getLiveDateYmd?: () => string;
  /** Effective processing/streaming poll interval for REST poll fallback. */
  getPollIntervalMs?: () => number;
  /**
   * ISO 8601 lower bound for the discovery poll, or null for the whole live day. Bounds the
   * `start_time` param on /fleet-performance-data so the id-discovery pass stops re-reading the
   * morning's aggregates on every tick. Omit for today's whole-day behaviour.
   */
  getWindowStartIso?: () => string | null;
}

let knownEventIds = new Set<number>();
let unsubscribeEventsAggregateReady: (() => void) | null = null;
let pollFallbackTimer: ReturnType<typeof setInterval> | null = null;
let handlingNotification = false;

export function seedLivePerformanceKnownEventIds(rows: LivePerformanceAggregateRow[]): void {
  knownEventIds = new Set(collectEventIds(rows));
}

export function resetLivePerformanceKnownEventIds(): void {
  knownEventIds = new Set();
  // The live SESSION is the right scope for "push has been proven", and this is its boundary (the
  // page unmounting). Leaving it set across sessions would let a working run yesterday throttle a
  // broken one today.
  livePushProven = false;
}

async function handleEventsAggregateReady(
  message: EventsAggregateReadyMessage,
  opts: LivePerformanceStreamBridgeOptions
): Promise<void> {
  if (handlingNotification) return;
  const projectId = opts.getProjectId();
  if (!projectId || message.project_id !== projectId) {
    return;
  }

  const newIds = (message.event_ids || [])
    .map((id) => Number(id))
    .filter((id) => Number.isInteger(id) && id > 0 && !knownEventIds.has(id));
  if (newIds.length === 0) {
    return;
  }

  handlingNotification = true;
  opts.onActiveChange?.(true);
  try {
    const channelsByType = opts.getChannelsByType();
    const filters = opts.buildFilters();
    const channelMapping = opts.getChannelMapping();
    const targets = opts.getTargets();

    const [rawAVG, rawSTD, rawAAV] = await Promise.all([
      channelsByType.AVG.length > 0
        ? fleetPerformanceDataService.fetchAggregatesByEventIds(newIds, channelsByType.AVG, 'AVG', filters)
        : Promise.resolve([]),
      channelsByType.STD.length > 0
        ? fleetPerformanceDataService.fetchAggregatesByEventIds(newIds, channelsByType.STD, 'STD', filters)
        : Promise.resolve([]),
      channelsByType.AAV.length > 0
        ? fleetPerformanceDataService.fetchAggregatesByEventIds(newIds, channelsByType.AAV, 'AAV', filters)
        : Promise.resolve([]),
    ]);

    const latestIncoming = inferLivePerformanceUwDwFromLatestRows(
      rawAVG as LivePerformanceAggregateRow[]
    );
    // True when the batch is for the other point of sail but the auto-switch is suppressed
    // (paused): keep the user's view, and keep these ids re-discoverable for when follow resumes.
    let uwDwSwitchSuppressed = false;
    if (latestIncoming && latestIncoming !== opts.getUwDw()) {
      if (opts.shouldAutoSwitchUwDw ? opts.shouldAutoSwitchUwDw() : true) {
        opts.setUwDw?.(latestIncoming);
      } else {
        uwDwSwitchSuppressed = true;
      }
    }

    const uwDw = opts.getUwDw();
    const twaGate = (rows: LivePerformanceAggregateRow[]) =>
      rows.filter((row) => passesLivePerformanceTwaGate(row, uwDw));

    const gatedAVG = twaGate(rawAVG as LivePerformanceAggregateRow[]);
    const gatedSTD = twaGate(rawSTD as LivePerformanceAggregateRow[]);
    const gatedAAV = twaGate(rawAAV as LivePerformanceAggregateRow[]);

    if (gatedAVG.length === 0 && gatedSTD.length === 0 && gatedAAV.length === 0) {
      if (!uwDwSwitchSuppressed) {
        newIds.forEach((id) => knownEventIds.add(id));
      }
      return;
    }

    const processType = async (
      raw: LivePerformanceAggregateRow[],
      existing: LivePerformanceAggregateRow[]
    ): Promise<LivePerformanceAggregateRow[]> => {
      if (raw.length === 0) return existing;
      const processed = performanceDataService.processPerformanceData(
        raw,
        [],
        targets,
        channelMapping
      );
      return mergeAggregateRows(existing, (processed.aggregates || []) as LivePerformanceAggregateRow[]);
    };

    const nextAVG = await processType(gatedAVG, opts.getAggregatesAVG());
    const nextSTD = await processType(gatedSTD, opts.getAggregatesSTD());
    const nextAAV = await processType(gatedAAV, opts.getAggregatesAAV());

    opts.setAggregatesAVG(nextAVG);
    opts.setAggregatesSTD(nextSTD);
    opts.setAggregatesAAV(nextAAV);
    opts.setAggregates(nextAVG);

    newIds.forEach((id) => knownEventIds.add(id));
    collectEventIds(nextAVG).forEach((id) => knownEventIds.add(id));
    collectEventIds(nextSTD).forEach((id) => knownEventIds.add(id));
    collectEventIds(nextAAV).forEach((id) => knownEventIds.add(id));

    const acceptedForSelection = [...gatedAVG, ...gatedSTD, ...gatedAAV];
    const latestId = latestEventIdByDatetime(acceptedForSelection);
    const highlightActive = opts.isHighlightActive ? opts.isHighlightActive() : true;
    // Emphasize the latest via the live highlight layer, NOT the selection store,
    // so the selection banner stays reserved for manual user selections. Keyed to
    // Follow: while paused the highlight must freeze so the page behaves like a
    // non-live Performance page.
    //
    // The AGGREGATE highlight layer specifically. These are BIN 10 aggregate ids; publishing them
    // through the maneuver layer (`setLiveHighlightEvents`) put Live Performance's latest point into
    // the maneuver tables, map and scatter, which is half of what the domain split exists to fix.
    if (highlightActive && latestId != null) {
      setLiveAggregateHighlightEvents([latestId]);
    }
    opts.setUpdateCharts(true);

    debug('[LivePerformanceStreamBridge] Appended events_aggregate rows', {
      newIds,
      avgAdded: gatedAVG.length,
      latestId,
      highlightActive,
    });
  } catch (err) {
    warn('[LivePerformanceStreamBridge] Failed to handle events_aggregate_ready', err as Error);
  } finally {
    handlingNotification = false;
    opts.onActiveChange?.(false);
  }
}

async function discoverNewEventIdsFromLiveFetch(
  opts: LivePerformanceStreamBridgeOptions
): Promise<number[]> {
  const liveDate = opts.getLiveDateYmd?.();
  if (!liveDate) {
    return [];
  }
  const channelsByType = opts.getChannelsByType();
  const filters = opts.buildFilters();
  // Time window: this pass only needs to discover *new* event ids, so the "last hour" bound turns
  // three whole-day reads per tick into three narrow ones. Null (= All) omits the param entirely.
  const startTime = opts.getWindowStartIso?.() ?? null;

  const [rawAVG, rawSTD, rawAAV] = await Promise.all([
    channelsByType.AVG.length > 0
      ? fleetPerformanceDataService.fetchAggregates(
          channelsByType.AVG,
          'AVG',
          liveDate,
          liveDate,
          filters,
          true,
          startTime
        )
      : Promise.resolve([]),
    channelsByType.STD.length > 0
      ? fleetPerformanceDataService.fetchAggregates(
          channelsByType.STD,
          'STD',
          liveDate,
          liveDate,
          filters,
          true,
          startTime
        )
      : Promise.resolve([]),
    channelsByType.AAV.length > 0
      ? fleetPerformanceDataService.fetchAggregates(
          channelsByType.AAV,
          'AAV',
          liveDate,
          liveDate,
          filters,
          true,
          startTime
        )
      : Promise.resolve([]),
  ]);

  const discovered = new Set<number>([
    ...collectEventIds(rawAVG as LivePerformanceAggregateRow[]),
    ...collectEventIds(rawSTD as LivePerformanceAggregateRow[]),
    ...collectEventIds(rawAAV as LivePerformanceAggregateRow[]),
  ]);

  return [...discovered].filter((id) => !knownEventIds.has(id));
}

async function pollLivePerformanceMerge(opts: LivePerformanceStreamBridgeOptions): Promise<void> {
  const projectId = opts.getProjectId();
  if (!projectId) {
    return;
  }
  try {
    const newIds = await discoverNewEventIdsFromLiveFetch(opts);
    if (newIds.length === 0) {
      return;
    }
    await handleEventsAggregateReady(
      {
        type: 'events_aggregate_ready',
        project_id: projectId,
        source_name: '',
        event_type: 'bin 10',
        event_ids: newIds,
      },
      opts
    );
  } catch (err) {
    warn('[LivePerformanceStreamBridge] Poll merge failed', err as Error);
  }
}

/**
 * One-shot catch-up: discover any events that arrived while follow was paused,
 * merge them, and (if follow is active) select the latest. Use on resume so the
 * page jumps straight to the live edge without a full reload.
 *
 * Deliberately ignores `isUpdateAllowed` — this call IS the resume action, so it must run even
 * when the gate is still reading paused (the caller fires it on the resume edge, and the
 * visibility handler fires it before any signal has settled).
 */
export async function catchUpLivePerformanceOnce(
  opts: LivePerformanceStreamBridgeOptions
): Promise<void> {
  await pollLivePerformanceMerge(opts);
}

/**
 * Once push is PROVEN the REST poll is only a backstop for dropped or pre-connection frames, so it
 * runs every Nth tick; the moment push stops being proven it reverts to full cadence (checked per
 * tick, no timer restart needed).
 *
 * The divisor is derived, not chosen: it lands the throttled cadence on `LIVE_PUSH_BACKSTOP_MS`
 * whatever the configured poll interval is, instead of leaving two magic numbers to drift apart.
 */
function wsBackstopTickDivisor(pollIntervalMs: number): number {
  return Math.max(1, Math.round(LIVE_PUSH_BACKSTOP_MS / Math.max(1000, pollIntervalMs)));
}

/**
 * Has at least one `events_aggregate_ready` frame actually arrived this session?
 *
 * This, and NOT `arrowStreamClient.isConnected()`, is what may throttle the REST poll. An open socket
 * only proves the transport is up; a connected-but-silent socket is exactly what a server that never
 * calls its broadcast looks like, and treating that as "push is working" left the page throttled to
 * a sixth of its cadence and quietly stale, with nothing on screen to say why.
 *
 * Deliberately sticky for the session: a frame having arrived proves the whole chain works, and a
 * momentary reconnect does not un-prove it. A genuine outage is caught by the backstop tick itself.
 */
let livePushProven = false;

function startLivePerformancePollFallback(
  opts: LivePerformanceStreamBridgeOptions,
  mode: 'primary' | 'ws-backstop' = 'primary'
): void {
  stopLivePerformancePollFallback();
  const intervalMs = Math.max(1000, opts.getPollIntervalMs?.() ?? 10000);
  let tick = 0;
  pollFallbackTimer = setInterval(() => {
    // Paused: sweep nothing. Checked BEFORE the backstop tick counter so a pause never desyncs
    // the 1-in-N throttle — a paused tick must not consume one of the divisor slots.
    if (opts.isUpdateAllowed && !opts.isUpdateAllowed()) {
      return;
    }
    if (mode === 'ws-backstop') {
      tick += 1;
      if (livePushProven && tick % wsBackstopTickDivisor(intervalMs) !== 0) {
        return;
      }
    }
    void pollLivePerformanceMerge(opts);
  }, intervalMs);
  debug('[LivePerformanceStreamBridge] REST poll fallback started', { intervalMs, mode });
}

function stopLivePerformancePollFallback(): void {
  if (pollFallbackTimer) {
    clearInterval(pollFallbackTimer);
    pollFallbackTimer = null;
  }
}

export async function startLivePerformanceStream(
  opts: LivePerformanceStreamBridgeOptions
): Promise<void> {
  stopLivePerformanceStream();

  if (!config.ENABLE_WEBSOCKETS) {
    debug('[LivePerformanceStreamBridge] WebSockets disabled — use startLivePerformanceUpdates');
    return;
  }

  const handler = (message: EventsAggregateReadyMessage) => {
    // A frame ARRIVED — that is what earns the REST poll its backstop cadence. See `livePushProven`.
    livePushProven = true;
    // Paused: drop the frame unconsumed. Its ids never enter `knownEventIds`, so the resume-side
    // catch-up re-discovers them over REST — the socket stays open and buffering meanwhile.
    if (opts.isUpdateAllowed && !opts.isUpdateAllowed()) {
      return;
    }
    void handleEventsAggregateReady(message, opts);
  };

  // Consume events_aggregate_ready on the arrow WS control channel (Phase D3 — off the legacy JSON WS).
  // ensureConnected opens an events-only arrow socket (no boat subscription needed for the tail).
  unsubscribeEventsAggregateReady = arrowStreamClient.onEventsAggregateReady(handler);
  arrowStreamClient.ensureConnected();
  debug('[LivePerformanceStreamBridge] arrow events tail started');
}

/**
 * Start live performance tail: WebSocket `events_aggregate_ready` when enabled,
 * otherwise REST merge poll at effective processing/streaming cadence.
 */
export async function startLivePerformanceUpdates(
  opts: LivePerformanceStreamBridgeOptions
): Promise<void> {
  stopLivePerformanceUpdates();

  if (config.ENABLE_WEBSOCKETS) {
    await startLivePerformanceStream(opts);
    // Backstop the WS tail with the REST merge poll. `events_aggregate_ready` is a
    // fire-and-forget broadcast to currently-connected clients with no replay
    // (columnarBroadcaster.broadcastEventsAggregateReady), so a frame emitted before
    // we connect — or dropped — is otherwise lost until the next phase job completes.
    // The poll discovers any missed event_ids at the processing cadence;
    // handleEventsAggregateReady dedupes via knownEventIds so overlapping delivery is
    // a no-op. Mirrors the always-poll design of the reliable Live Maneuvers tail.
    if (opts.getLiveDateYmd && opts.getPollIntervalMs) {
      startLivePerformancePollFallback(opts, 'ws-backstop');
    } else {
      debug('[LivePerformanceStreamBridge] WS tail started without REST backstop — missing poll options');
    }
    return;
  }

  if (!opts.getLiveDateYmd || !opts.getPollIntervalMs) {
    debug('[LivePerformanceStreamBridge] WebSockets disabled — missing poll options for REST fallback');
    return;
  }

  startLivePerformancePollFallback(opts);
}

export function stopLivePerformanceStream(): void {
  if (unsubscribeEventsAggregateReady) {
    unsubscribeEventsAggregateReady();
    unsubscribeEventsAggregateReady = null;
  }
}

export function stopLivePerformanceUpdates(): void {
  stopLivePerformanceStream();
  stopLivePerformancePollFallback();
}
